import React, { useEffect, useRef, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import * as echarts from "echarts";

const EventTimelineBarChart = ({ barChartData, height, tooltipCustomOption = undefined, barWidth = 20 }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // 1. 處理數據與時間映射
  const { dynamicMax, filterData, startTimestamp } = useMemo(() => {
    const now = new Date();
    const currentHour = new Date(now);
    currentHour.setMinutes(0, 0, 0, 0);
    const currentTs = currentHour.getTime();
    const startTs = currentTs - 23 * 60 * 60 * 1000;

    // 解析數據
    const seriesData = barChartData.series[0].data.map(d => {
      const localDate = new Date(d[0]);
      return [localDate.getTime(), d[1]];
    });

    // 將數據映射到 0-23 小時刻度
    const finalData = [];
    for (let i = 0; i < 24; i++) {
      const ts = startTs + i * 3600 * 1000;
      const match = seriesData.find(d => Math.abs(d[0] - ts) < 1000);
      finalData.push([i, match ? match[1] : 0]);
    }

    const maxValue = finalData.length > 0 ? Math.max(...finalData.map(d => d[1])) : 0;
    const currentMax = maxValue <= 5 ? 10 : Math.ceil(maxValue / 5) * 5;

    return {
      dynamicMax: currentMax,
      filterData: finalData,
      startTimestamp: startTs
    };
  }, [barChartData]);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    let chart = echarts.getInstanceByDom(chartDom);
    if (chart) { chart.dispose(); }
    chartDom.innerHTML = '';

    chart = echarts.init(chartDom, null, { renderer: 'svg' });
    chartInstance.current = chart;

    const defaultTooltip = {
      show: true,
      trigger: "item",
      backgroundColor: theme.palette.primary.greyPrimaryTransparency,
      textStyle: { color: "#fff" },
      formatter: (params) => {
        if (!params || !params.value) return "";
        const d = new Date(startTimestamp + params.value[0] * 3600 * 1000);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `
          <div style="font-size: 12px; line-height: 1.5;">
            <div>${hh}:${mm}</div>
            <div style="font-weight: bold;">Count: ${params.value[1]}</div>
          </div>
        `;
      }
    };

    const option = {
      tooltip: tooltipCustomOption === null ? { show: false } : { ...defaultTooltip, ...tooltipCustomOption },
      grid: {
        left: 50,
        right: 40,
        top: 20,
        bottom: 30,
        containLabel: false,
      },
      xAxis: {
        type: "value",
        min: 0,
        max: 23,
        interval: 4, // 強制刻度間隔
        axisLabel: {
          formatter: (value) => {
            const d = new Date(startTimestamp + value * 3600 * 1000);
            const hh = String(d.getHours()).padStart(2, '0');
            return `${hh}:00`;
          },
          margin: 12,
          color: "#999",
          fontSize: 11,
        },
        axisLine: { show: true, lineStyle: { color: "#e0e0e0" } },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: "dotted", color: "#e0e0e0" } }
      },
      yAxis: {
        type: "value",
        min: 0,
        max: dynamicMax,
        minInterval: 1, // 移除強制間隔 5，改為自動計算整數刻度
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: "dotted", color: "#e0e0e0" } },
        axisLabel: { color: "#999", margin: 15 }
      },
      series: [{
        name: barChartData.series[0].name,
        type: "bar",
        data: filterData,
        barWidth: barWidth,
        itemStyle: {
          color: "#5470c6",
          borderRadius: [0, 0, 0, 0]
        }
      }]
    };

    chart.setOption(option);
    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartDom);
    return () => { resizeObserver.disconnect(); chart.dispose(); };
  }, [barChartData, theme, tooltipCustomOption, dynamicMax, filterData, startTimestamp, barWidth]);

  return <Box ref={chartRef} style={{ width: "100%", height, background: "#fff" }} />;
};

export default EventTimelineBarChart;